import { useMemo } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { ContractorStepDetailsCardStatus } from 'src/components/contractorStepDetailsCard/types'
import { mapCollectedDataStatusToCardStatus } from 'src/components/contractorStepDetailsCard/utils'
import type { SubmissionBlock } from 'src/components/contractorStepDetailsCard/components/ContractorStepDetailsCardContent'
import { getContractorStepItemState } from './useContractorStepItemState'
import { getContractorStepDetailsBlocksVisibility } from './useContractorStepDetailsBlocksVisibility'
import { StepActionsMenu } from '../../contractor-details/step-actions/StepActionsMenu'

dayjs.extend(utc)

const DATE_FORMAT = 'MM/DD/YYYY'

export interface UseStepContentBlocksParams {
  collectedDataItems: CollectedData[]
  contractorDetails: ContractorDetails
  partnerId: string | null
  isRequestApprove?: boolean
  isSubcontractor?: boolean
  onRefreshContractors: () => void | Promise<void>
  openModalForItem: (itemId: string) => void
}

export function useStepContentBlocks({
  collectedDataItems,
  contractorDetails,
  partnerId,
  isRequestApprove,
  isSubcontractor,
  onRefreshContractors,
  openModalForItem,
}: UseStepContentBlocksParams): SubmissionBlock[] {
  return useMemo(() => {
    return collectedDataItems.map((item): SubmissionBlock => {
      const state = getContractorStepItemState({
        collectedData: item,
        contractorDetails,
        isRequestApprove,
        isSubcontractor,
      })
      const visibility = getContractorStepDetailsBlocksVisibility({
        collectedData: item,
        contractorDetails,
        stepCanBeRecollected: state.stepCanBeRecollected,
        stepStatus: state.stepStatus,
        partnerId,
        isRequestApprove,
        isSubcontractor,
      })

      const status = state.expiresSoon
        ? ContractorStepDetailsCardStatus.ExpiringSoon
        : mapCollectedDataStatusToCardStatus(state.stepStatus)

      const showActions = visibility.showSetExpirationButton || visibility.showDeclineButton

      return {
        status,
        showReviewButtonWithText: visibility.isWaitingForApproval,
        expiresAt: state.expiresAt ? dayjs(state.expiresAt).utc().format(DATE_FORMAT) : undefined,
        effectiveAt: item.effectiveAt
          ? dayjs(item.effectiveAt).utc().format(DATE_FORMAT)
          : undefined,
        collectedData: item,
        onReviewStepDataAction: visibility.showDetails
          ? () => openModalForItem(item.id)
          : undefined,
        actions: showActions ? (
          <StepActionsMenu
            collectedData={item}
            contractorDetails={contractorDetails}
            showDeclineOption={visibility.showDeclineButton}
            showExpirationOption={visibility.showSetExpirationButton}
            onAction={onRefreshContractors}
          />
        ) : undefined,
        showRecollectButton: onRefreshContractors != null && visibility.showRecollectButton,
      }
    })
  }, [
    collectedDataItems,
    contractorDetails,
    partnerId,
    isRequestApprove,
    isSubcontractor,
    onRefreshContractors,
    openModalForItem,
  ])
}
